
import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  loading: boolean; // Alias for isLoading
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>; // Add signUp method
  signOut: () => Promise<void>;
  isAdmin: boolean;
  checkUserStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setIsLoading(true);
    
    // Set up auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.info("Auth state changed:", event);
        
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          await checkUserStatus();
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
        }
        
        setIsLoading(false);
      }
    );

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (initialSession) {
          setSession(initialSession);
          setUser(initialSession.user);
          await checkUserStatus();
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkUserStatus = async () => {
    if (!user) return;

    console.info("Checking user status for:", user.id);

    try {
      // Check if user has a profile
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }

      if (userProfile) {
        setProfile(userProfile);
      } else {
        // If user has no profile yet, create it based on user metadata
        try {
          const { data: userData } = await supabase.auth.getUser();
          const metadata = userData?.user?.user_metadata;
          
          if (metadata) {
            const { error: insertError } = await supabase
              .from('user_profiles')
              .insert({
                user_id: user.id,
                name: metadata.name || user.email?.split('@')[0] || 'User',
                email: user.email || '',
              });
              
            if (insertError) {
              console.error("Error creating user profile:", insertError);
            } else {
              // Fetch the newly created profile
              const { data: newProfile } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('user_id', user.id)
                .maybeSingle();
                
              if (newProfile) {
                setProfile(newProfile);
              }
            }
          }
        } catch (error) {
          console.error("Error in profile creation:", error);
        }
      }
      
      // Check if user has vita_credits, if not create them
      const { data: vitaCredits, error: vitaCreditsError } = await supabase
        .from('vita_credits')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (vitaCreditsError || !vitaCredits) {
        // Create vita_credits for the user
        const { error: insertError } = await supabase
          .from('vita_credits')
          .insert({
            user_id: user.id,
            credits_used: 0,
            credits_limit: 100
          });
          
        if (insertError) {
          console.error("Error creating vita_credits:", insertError);
        }
      }
      
      // Check if user has a subscription, if not create one
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (subscriptionError || !subscription) {
        // Create a subscription for the user
        const { error: insertError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: user.id,
            plan: 'basic',
            status: 'trialing',
            trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          });
          
        if (insertError) {
          console.error("Error creating subscription:", insertError);
        }
      }
    } catch (error) {
      console.error("Error in checkUserStatus:", error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Perform additional checks or redirect as needed
      if (data?.session) {
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast({
        title: "Sign In Error",
        description: error.message || "Failed to sign in",
        variant: "destructive",
      });
    }
  };

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      // Create user in Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name },
          emailRedirectTo: `${window.location.origin}/auth?mode=email_confirmed`
        },
      });

      if (error) throw error;

      toast({
        title: "Verification Email Sent",
        description: "Please check your email to verify your account.",
      });

      // Redirect to the verification page or handle in Auth component
      navigate('/auth?mode=verification_sent&email=' + encodeURIComponent(email));
    } catch (error: any) {
      toast({
        title: "Registration Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Sign Out Error",
        description: error.message || "Failed to sign out",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        isLoading,
        loading: isLoading, // Alias for isLoading
        signIn,
        signUp,
        signOut,
        isAdmin,
        checkUserStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
